import React, { useEffect, useState } from "react";
import { Card, Row, Col, Select, DatePicker, Button, Table, message, Space, Statistic } from "antd";
import { DownloadOutlined, ArrowUpOutlined, ArrowDownOutlined } from "@ant-design/icons";
import API from "../../../api";
import dayjs from "dayjs";
import * as XLSX from 'xlsx';

const { Option } = Select;
const { RangePicker } = DatePicker;

const BalanceSheet = () => {
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [selectedDates, setSelectedDates] = useState([]);
  const [selectedFinancialYear, setSelectedFinancialYear] = useState(null);
  const [loading, setLoading] = useState(false);
  const [balanceSheet, setBalanceSheet] = useState(null);
  const [incomeStatement, setIncomeStatement] = useState(null);
  const [ratios, setRatios] = useState(null);

  // Fetch stores for the dropdown
  useEffect(() => {
    const fetchStores = async () => {
      try {
        const response = await API.get("/stores");
        setStores(response?.data || []);
      } catch (error) {
        message.error("Failed to fetch stores.");
      }
    };
    fetchStores();
  }, []);

  // Generate financial years
  const generateFinancialYears = () => {
    const currentDate = new Date();
    const financialYearStart = new Date(currentDate.getFullYear(), 3, 1); // April 1st
    let currentFinancialYearStart;
    if (currentDate < financialYearStart) {
      currentFinancialYearStart = currentDate.getFullYear() - 1;
    } else {
      currentFinancialYearStart = currentDate.getFullYear();
    }
    const financialYears = [];
    let startYear = 2024;
    let endYear = startYear + 1;
    while (startYear <= currentFinancialYearStart) {
      financialYears.push(`${startYear}-${endYear}`);
      startYear++;
      endYear++;
    }
    return financialYears;
  };

  // Fetch Balance Sheet
  const fetchBalanceSheet = async () => {
    setLoading(true);
    try {
      const params = {
        ...(selectedStore && { storeId: selectedStore }),
        ...(selectedDates.length === 2 && {
          fromDate: selectedDates[0].format("YYYY-MM-DD"),
          toDate: selectedDates[1].format("YYYY-MM-DD"),
        }),
        ...(selectedFinancialYear && { financialYear: selectedFinancialYear }),
      };
      const response = await API.get("/accounts/balance-sheet", { params });
      console.log("Balance Sheet Response:", response.data); // Debug log
      
      if (response.data.success) {
        setBalanceSheet(response.data.data.balanceSheet);
        setIncomeStatement(response.data.data.incomeStatement);
        setRatios(response.data.data.ratios);
      } else {
        message.error("Failed to fetch balance sheet.");
        setBalanceSheet(null);
        setIncomeStatement(null);
        setRatios(null);
      }
    } catch (error) {
      console.error("Balance Sheet Error:", error);
      message.error("Failed to fetch balance sheet.");
      setBalanceSheet(null);
      setIncomeStatement(null);
      setRatios(null);
    } finally {
      setLoading(false);
    }
  };

  // Download Balance Sheet as Excel
  const downloadBalanceSheet = () => {
    if (!balanceSheet) {
      message.warning("No data available to download");
      return;
    }

    try {
      // Create workbook
      const wb = XLSX.utils.book_new();

      // Assets Worksheet
      const assetsData = [
        { 'Item': 'ASSETS', 'Amount (₹)': '', 'Details': '' },
        { 'Item': 'Current Assets', 'Amount (₹)': '', 'Details': '' },
        { 'Item': '  Cash and Bank', 'Amount (₹)': balanceSheet.assets.currentAssets.cashAndBank?.toFixed(2) || '0.00', 'Details': 'Available cash and bank balance' },
        { 'Item': '  Accounts Receivable', 'Amount (₹)': balanceSheet.assets.currentAssets.accountsReceivable?.toFixed(2) || '0.00', 'Details': 'Amount owed by customers' },
        { 'Item': '  Inventory', 'Amount (₹)': balanceSheet.assets.currentAssets.inventory?.toFixed(2) || '0.00', 'Details': 'Current stock value' },
        { 'Item': '  Prepaid Expenses', 'Amount (₹)': balanceSheet.assets.currentAssets.prepaidExpenses?.toFixed(2) || '0.00', 'Details': 'Advance payments' },
        { 'Item': 'Total Current Assets', 'Amount (₹)': balanceSheet.assets.currentAssets.totalCurrentAssets?.toFixed(2) || '0.00', 'Details': '' },
        { 'Item': '', 'Amount (₹)': '', 'Details': '' },
        { 'Item': 'Fixed Assets', 'Amount (₹)': '', 'Details': '' },
        { 'Item': '  Equipment', 'Amount (₹)': balanceSheet.assets.fixedAssets.equipment?.toFixed(2) || '0.00', 'Details': 'Machinery and equipment' },
        { 'Item': '  Furniture', 'Amount (₹)': balanceSheet.assets.fixedAssets.furniture?.toFixed(2) || '0.00', 'Details': 'Office furniture and fixtures' },
        { 'Item': '  Vehicles', 'Amount (₹)': balanceSheet.assets.fixedAssets.vehicles?.toFixed(2) || '0.00', 'Details': 'Company vehicles' },
        { 'Item': 'Total Fixed Assets', 'Amount (₹)': balanceSheet.assets.fixedAssets.totalFixedAssets?.toFixed(2) || '0.00', 'Details': '' },
        { 'Item': '', 'Amount (₹)': '', 'Details': '' },
        { 'Item': 'TOTAL ASSETS', 'Amount (₹)': balanceSheet.assets.totalAssets?.toFixed(2) || '0.00', 'Details': '' },
      ];

      const wsAssets = XLSX.utils.json_to_sheet(assetsData);
      wsAssets['!cols'] = [{ wch: 25 }, { wch: 15 }, { wch: 40 }];
      XLSX.utils.book_append_sheet(wb, wsAssets, 'Assets');

      // Liabilities Worksheet
      const liabilitiesData = [
        { 'Item': 'LIABILITIES', 'Amount (₹)': '', 'Details': '' },
        { 'Item': 'Current Liabilities', 'Amount (₹)': '', 'Details': '' },
        { 'Item': '  Accounts Payable', 'Amount (₹)': balanceSheet.liabilities.currentLiabilities.accountsPayable?.toFixed(2) || '0.00', 'Details': 'Amount owed to suppliers' },
        { 'Item': '  Short Term Loans', 'Amount (₹)': balanceSheet.liabilities.currentLiabilities.shortTermLoans?.toFixed(2) || '0.00', 'Details': 'Short term borrowings' },
        { 'Item': '  Accrued Expenses', 'Amount (₹)': balanceSheet.liabilities.currentLiabilities.accruedExpenses?.toFixed(2) || '0.00', 'Details': 'Expenses incurred but not paid' },
        { 'Item': 'Total Current Liabilities', 'Amount (₹)': balanceSheet.liabilities.currentLiabilities.totalCurrentLiabilities?.toFixed(2) || '0.00', 'Details': '' },
        { 'Item': '', 'Amount (₹)': '', 'Details': '' },
        { 'Item': 'Long Term Liabilities', 'Amount (₹)': '', 'Details': '' },
        { 'Item': '  Long Term Loans', 'Amount (₹)': balanceSheet.liabilities.longTermLiabilities.longTermLoans?.toFixed(2) || '0.00', 'Details': 'Long term borrowings' },
        { 'Item': 'Total Long Term Liabilities', 'Amount (₹)': balanceSheet.liabilities.longTermLiabilities.totalLongTermLiabilities?.toFixed(2) || '0.00', 'Details': '' },
        { 'Item': '', 'Amount (₹)': '', 'Details': '' },
        { 'Item': 'TOTAL LIABILITIES', 'Amount (₹)': balanceSheet.liabilities.totalLiabilities?.toFixed(2) || '0.00', 'Details': '' },
      ];

      const wsLiabilities = XLSX.utils.json_to_sheet(liabilitiesData);
      wsLiabilities['!cols'] = [{ wch: 25 }, { wch: 15 }, { wch: 40 }];
      XLSX.utils.book_append_sheet(wb, wsLiabilities, 'Liabilities');

      // Equity Worksheet
      const equityData = [
        { 'Item': 'EQUITY', 'Amount (₹)': '', 'Details': '' },
        { 'Item': '  Owner Equity', 'Amount (₹)': balanceSheet.equity.ownerEquity?.toFixed(2) || '0.00', 'Details': 'Owner investment and retained earnings' },
        { 'Item': '  Retained Earnings', 'Amount (₹)': balanceSheet.equity.retainedEarnings?.toFixed(2) || '0.00', 'Details': 'Accumulated profits' },
        { 'Item': 'TOTAL EQUITY', 'Amount (₹)': balanceSheet.equity.totalEquity?.toFixed(2) || '0.00', 'Details': '' },
        { 'Item': '', 'Amount (₹)': '', 'Details': '' },
        { 'Item': 'TOTAL LIABILITIES & EQUITY', 'Amount (₹)': (balanceSheet.liabilities.totalLiabilities + balanceSheet.equity.totalEquity)?.toFixed(2) || '0.00', 'Details': '' },
      ];

      const wsEquity = XLSX.utils.json_to_sheet(equityData);
      wsEquity['!cols'] = [{ wch: 25 }, { wch: 15 }, { wch: 40 }];
      XLSX.utils.book_append_sheet(wb, wsEquity, 'Equity');

      // Generate filename
      const storeName = stores.find(s => s._id === selectedStore)?.name || 'All Stores';
      const dateRange = selectedDates.length === 2 
        ? `${selectedDates[0].format('DD-MM-YYYY')}_to_${selectedDates[1].format('DD-MM-YYYY')}`
        : 'All Dates';
      const filename = `Balance_Sheet_${storeName}_${dateRange}_${dayjs().format('DD-MM-YYYY')}.xlsx`;

      // Download file
      XLSX.writeFile(wb, filename);
      message.success('Balance Sheet downloaded successfully!');
    } catch (error) {
      console.error('Error downloading Balance Sheet:', error);
      message.error('Failed to download Balance Sheet');
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col xs={24} sm={8} md={6} lg={5}>
            <Select
              placeholder="Select Store"
              value={selectedStore}
              onChange={setSelectedStore}
              allowClear
              style={{ width: "100%" }}
            >
              {stores.map((store) => (
                <Option key={store._id} value={store._id}>
                  {store.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={10} md={8} lg={7}>
            <RangePicker
              value={selectedDates}
              onChange={setSelectedDates}
              style={{ width: "100%" }}
              format="DD-MM-YYYY"
              allowClear
            />
          </Col>
          <Col xs={24} sm={8} md={6} lg={5}>
            <Select
              placeholder="Select Financial Year"
              value={selectedFinancialYear}
              onChange={setSelectedFinancialYear}
              allowClear
              style={{ width: "100%" }}
            >
              {generateFinancialYears().map((year) => (
                <Option key={year} value={year}>
                  {year}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={6} md={4} lg={3}>
            <Space>
              <Button type="primary" onClick={fetchBalanceSheet} loading={loading}>
                Get Report
              </Button>
              <Button
                onClick={() => {
                  setSelectedStore(null);
                  setSelectedDates([]);
                  setSelectedFinancialYear(null);
                  setBalanceSheet(null);
                  setIncomeStatement(null);
                  setRatios(null);
                }}
              >
                Reset
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Summary Statistics */}
      {balanceSheet && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="Total Assets"
                value={balanceSheet.assets.totalAssets}
                precision={2}
                valueStyle={{ color: '#3f8600' }}
                prefix="₹"
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Total Liabilities"
                value={balanceSheet.liabilities.totalLiabilities}
                precision={2}
                valueStyle={{ color: '#cf1322' }}
                prefix="₹"
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Total Equity"
                value={balanceSheet.equity.totalEquity}
                precision={2}
                valueStyle={{ color: '#1890ff' }}
                prefix="₹"
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Net Worth"
                value={balanceSheet.assets.totalAssets - balanceSheet.liabilities.totalLiabilities}
                precision={2}
                valueStyle={{ color: '#722ed1' }}
                prefix="₹"
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Assets Section */}
      <Card
        title="Assets"
        style={{ marginBottom: 24 }}
        extra={
          balanceSheet && (
            <Button 
              type="primary" 
              icon={<DownloadOutlined />} 
              onClick={downloadBalanceSheet}
            >
              Download Excel
            </Button>
          )
        }
      >
        {balanceSheet ? (
          <div>
            <Row gutter={16}>
              <Col span={12}>
                <Card title="Current Assets" size="small">
                  <Row gutter={16}>
                    <Col span={12}>
                      <Statistic
                        title="Cash & Bank"
                        value={balanceSheet.assets.currentAssets.cashAndBank}
                        precision={2}
                        prefix="₹"
                      />
                    </Col>
                    <Col span={12}>
                      <Statistic
                        title="Accounts Receivable"
                        value={balanceSheet.assets.currentAssets.accountsReceivable}
                        precision={2}
                        prefix="₹"
                      />
                    </Col>
                  </Row>
                  <Row gutter={16} style={{ marginTop: 16 }}>
                    <Col span={12}>
                      <Statistic
                        title="Inventory"
                        value={balanceSheet.assets.currentAssets.inventory}
                        precision={2}
                        prefix="₹"
                      />
                    </Col>
                    <Col span={12}>
                      <Statistic
                        title="Prepaid Expenses"
                        value={balanceSheet.assets.currentAssets.prepaidExpenses}
                        precision={2}
                        prefix="₹"
                      />
                    </Col>
                  </Row>
                  <div style={{ marginTop: 16, padding: '8px', backgroundColor: '#f6ffed', borderRadius: '4px' }}>
                    <Statistic
                      title="Total Current Assets"
                      value={balanceSheet.assets.currentAssets.totalCurrentAssets}
                      precision={2}
                      valueStyle={{ color: '#52c41a', fontWeight: 'bold' }}
                      prefix="₹"
                    />
                  </div>
                </Card>
              </Col>
              <Col span={12}>
                <Card title="Fixed Assets" size="small">
                  <Row gutter={16}>
                    <Col span={12}>
                      <Statistic
                        title="Equipment"
                        value={balanceSheet.assets.fixedAssets.equipment}
                        precision={2}
                        prefix="₹"
                      />
                    </Col>
                    <Col span={12}>
                      <Statistic
                        title="Furniture"
                        value={balanceSheet.assets.fixedAssets.furniture}
                        precision={2}
                        prefix="₹"
                      />
                    </Col>
                  </Row>
                  <Row gutter={16} style={{ marginTop: 16 }}>
                    <Col span={12}>
                      <Statistic
                        title="Vehicles"
                        value={balanceSheet.assets.fixedAssets.vehicles}
                        precision={2}
                        prefix="₹"
                      />
                    </Col>
                    <Col span={12}>
                      <Statistic
                        title="Other Assets"
                        value={0}
                        precision={2}
                        prefix="₹"
                      />
                    </Col>
                  </Row>
                  <div style={{ marginTop: 16, padding: '8px', backgroundColor: '#f6ffed', borderRadius: '4px' }}>
                    <Statistic
                      title="Total Fixed Assets"
                      value={balanceSheet.assets.fixedAssets.totalFixedAssets}
                      precision={2}
                      valueStyle={{ color: '#52c41a', fontWeight: 'bold' }}
                      prefix="₹"
                    />
                  </div>
                </Card>
              </Col>
            </Row>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
            {loading ? 'Loading...' : 'No data available. Please select filters and click "Get Report".'}
          </div>
        )}
      </Card>

      {/* Liabilities Section */}
      <Card title="Liabilities" style={{ marginBottom: 24 }}>
        {balanceSheet ? (
          <div>
            <Row gutter={16}>
              <Col span={12}>
                <Card title="Current Liabilities" size="small">
                  <Row gutter={16}>
                    <Col span={12}>
                      <Statistic
                        title="Accounts Payable"
                        value={balanceSheet.liabilities.currentLiabilities.accountsPayable}
                        precision={2}
                        prefix="₹"
                      />
                    </Col>
                    <Col span={12}>
                      <Statistic
                        title="Short Term Loans"
                        value={balanceSheet.liabilities.currentLiabilities.shortTermLoans}
                        precision={2}
                        prefix="₹"
                      />
                    </Col>
                  </Row>
                  <Row gutter={16} style={{ marginTop: 16 }}>
                    <Col span={12}>
                      <Statistic
                        title="Accrued Expenses"
                        value={balanceSheet.liabilities.currentLiabilities.accruedExpenses}
                        precision={2}
                        prefix="₹"
                      />
                    </Col>
                    <Col span={12}>
                      <Statistic
                        title="Other Current Liabilities"
                        value={0}
                        precision={2}
                        prefix="₹"
                      />
                    </Col>
                  </Row>
                  <div style={{ marginTop: 16, padding: '8px', backgroundColor: '#fff2e8', borderRadius: '4px' }}>
                    <Statistic
                      title="Total Current Liabilities"
                      value={balanceSheet.liabilities.currentLiabilities.totalCurrentLiabilities}
                      precision={2}
                      valueStyle={{ color: '#fa541c', fontWeight: 'bold' }}
                      prefix="₹"
                    />
                  </div>
                </Card>
              </Col>
              <Col span={12}>
                <Card title="Long Term Liabilities" size="small">
                  <Row gutter={16}>
                    <Col span={12}>
                      <Statistic
                        title="Long Term Loans"
                        value={balanceSheet.liabilities.longTermLiabilities.longTermLoans}
                        precision={2}
                        prefix="₹"
                      />
                    </Col>
                    <Col span={12}>
                      <Statistic
                        title="Other Long Term"
                        value={0}
                        precision={2}
                        prefix="₹"
                      />
                    </Col>
                  </Row>
                  <div style={{ marginTop: 16, padding: '8px', backgroundColor: '#fff2e8', borderRadius: '4px' }}>
                    <Statistic
                      title="Total Long Term Liabilities"
                      value={balanceSheet.liabilities.longTermLiabilities.totalLongTermLiabilities}
                      precision={2}
                      valueStyle={{ color: '#fa541c', fontWeight: 'bold' }}
                      prefix="₹"
                    />
                  </div>
                </Card>
              </Col>
            </Row>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
            {loading ? 'Loading...' : 'No data available. Please select filters and click "Get Report".'}
          </div>
        )}
      </Card>

      {/* Equity Section */}
      <Card title="Equity" style={{ marginBottom: 24 }}>
        {balanceSheet ? (
          <Row gutter={16}>
            <Col span={8}>
              <Statistic
                title="Owner Equity"
                value={balanceSheet.equity.ownerEquity}
                precision={2}
                valueStyle={{ color: '#1890ff' }}
                prefix="₹"
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="Retained Earnings"
                value={balanceSheet.equity.retainedEarnings}
                precision={2}
                valueStyle={{ color: '#1890ff' }}
                prefix="₹"
              />
            </Col>
            <Col span={8}>
              <div style={{ padding: '8px', backgroundColor: '#e6f7ff', borderRadius: '4px' }}>
                <Statistic
                  title="Total Equity"
                  value={balanceSheet.equity.totalEquity}
                  precision={2}
                  valueStyle={{ color: '#1890ff', fontWeight: 'bold' }}
                  prefix="₹"
                />
              </div>
            </Col>
          </Row>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
            {loading ? 'Loading...' : 'No data available. Please select filters and click "Get Report".'}
          </div>
        )}
      </Card>

      {/* Financial Ratios */}
      {ratios && (
        <Card title="Financial Ratios" style={{ marginBottom: 24 }}>
          <Row gutter={16}>
            <Col span={8}>
              <Statistic
                title="Current Ratio"
                value={ratios.currentRatio}
                precision={2}
                valueStyle={{ color: ratios.currentRatio > 1 ? '#3f8600' : '#cf1322' }}
                suffix=":1"
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="Debt to Equity Ratio"
                value={ratios.debtToEquityRatio}
                precision={2}
                valueStyle={{ color: ratios.debtToEquityRatio < 1 ? '#3f8600' : '#cf1322' }}
                suffix=":1"
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="Return on Equity"
                value={ratios.returnOnEquity * 100}
                precision={2}
                valueStyle={{ color: ratios.returnOnEquity > 0 ? '#3f8600' : '#cf1322' }}
                suffix="%"
              />
            </Col>
          </Row>
        </Card>
      )}

      {/* Income Statement Summary */}
      {incomeStatement && (
        <Card title="Income Statement Summary" style={{ marginBottom: 24 }}>
          <Row gutter={16}>
            <Col span={6}>
              <Statistic
                title="Total Revenue"
                value={incomeStatement.revenue.totalRevenue}
                precision={2}
                valueStyle={{ color: '#3f8600' }}
                prefix="₹"
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Cost of Goods Sold"
                value={incomeStatement.expenses.costOfGoodsSold}
                precision={2}
                valueStyle={{ color: '#cf1322' }}
                prefix="₹"
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Operating Expenses"
                value={incomeStatement.expenses.operatingExpenses}
                precision={2}
                valueStyle={{ color: '#cf1322' }}
                prefix="₹"
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Net Income"
                value={incomeStatement.netIncome}
                precision={2}
                valueStyle={{ color: incomeStatement.netIncome > 0 ? '#3f8600' : '#cf1322' }}
                prefix="₹"
              />
            </Col>
          </Row>
        </Card>
      )}
    </div>
  );
};

export default BalanceSheet;

